import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment'; // Already present
// Add Row, Col, Tooltip, Typography to import
import { Form, Button, Select, Input, message, Spin, Alert, Typography, Card, Tooltip, Space, Row, Col } from 'antd';
import { SaveOutlined, CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext'; // Already present

const { Option } = Select;
const { Text, Title } = Typography; // Added Title
const { TextArea } = Input;

// Define the vaccines to track - Added new ones
const VACCINE_LIST = [
    'tdap', 'vzv', 'hepB', 'bcg', 'ipv', 'mmr', 'mcv',
    'flu', 'covid19', 'pcv', 'hpv', 'hib', 'je', 'rota'
];
// Map keys to display names
const VACCINE_DISPLAY_NAMES = {
    tdap: 'Tdap', vzv: 'Varicella (VZV)', hepB: 'Hepatitis B', bcg: 'BCG',
    ipv: 'Polio (IPV)', mmr: 'MMR', mcv: 'Meningococcal (MCV)', flu: 'Influenza (Flu)',
    covid19: 'COVID-19', pcv: 'Pneumococcal (PCV)', hpv: 'HPV', hib: 'Hib',
    je: 'Japanese Encephalitis', rota: 'Rotavirus'
};
const STATUS_OPTIONS = ['Given', 'Not Given', 'Refused', 'Contraindicated', 'Unknown'];

const VaccinationStatusSection = ({ patientId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Removed initialStatus, using form.getFieldsValue() for comparison if needed later
  const [vaccineStatuses, setVaccineStatuses] = useState({}); // State to track status for conditional rendering
  const [lastReviewed, setLastReviewed] = useState(null); // Added state
  const [isEditMode, setIsEditMode] = useState(false); // Add edit mode state
  const { user } = useAuth(); // Already present

  // Fetch current vaccination status
  const fetchStatus = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch patient data, populating review user
      const response = await axios.get(`/patients/${patientId}?populate=vaccinationLastReviewed.user`);
      const patientData = response.data.data.patient;
      const fetchedVaccinationStatus = patientData?.vaccinationStatus || {};
      setLastReviewed(patientData?.vaccinationLastReviewed || null); // Set review status

      // Prepare initial form values and status state
      const initialFormValues = {};
      const initialStatuses = {};
      VACCINE_LIST.forEach(key => {
        const statusData = fetchedVaccinationStatus[key] || {}; // Get {status, year} or {}
        initialFormValues[`${key}_status`] = statusData.status || 'Unknown'; // Default to Unknown
        initialFormValues[`${key}_year`] = statusData.year || '';
        initialStatuses[key] = statusData.status || 'Unknown';
      });
      // Include 'other' notes
      initialFormValues.other = fetchedVaccinationStatus.other || '';

      form.setFieldsValue(initialFormValues);
      setVaccineStatuses(initialStatuses); // Initialize status tracking state

    } catch (err) {
      console.error("Error fetching vaccination status:", err);
      setError(err.response?.data?.message || 'Failed to fetch vaccination status.');
      setVaccineStatuses({});
      setLastReviewed(null); // Clear on error
    } finally {
      setLoading(false);
    }
  }, [patientId, form]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Handle Save Status
  const handleSaveStatus = async (formValues) => {
    setLoading(true);
    setError(null);

    // Transform flat form values back to nested structure for backend
    const payload = { vaccinationStatus: {} };
    VACCINE_LIST.forEach(key => {
      const status = formValues[`${key}_status`];
      const year = formValues[`${key}_year`];
      payload.vaccinationStatus[key] = {
        status: status || 'Unknown', // Ensure status is always sent
        // Only include year if status is 'Given' and year has a value
        ...(status === 'Given' && year && { year: year })
      };
    });
    // Include 'other' notes
    payload.vaccinationStatus.other = formValues.other || '';

    try {
      await axios.patch(`/patients/${patientId}`, payload);
      message.success('Vaccination status updated successfully!');
      // Re-sync the status state after successful save
      const newStatuses = {};
      VACCINE_LIST.forEach(key => {
        newStatuses[key] = payload.vaccinationStatus[key].status;
      });
      setVaccineStatuses(newStatuses);
      setIsEditMode(false); // Exit edit mode after successful save
      // Optionally re-fetch if needed, but direct update is faster
      // fetchStatus();
    } catch (err) {
      console.error("Error saving vaccination status:", err);
      message.error(err.response?.data?.message || 'Failed to save status.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for form value changes to update status state
  const handleValuesChange = (changedValues, allValues) => {
    const newStatuses = { ...vaccineStatuses };
    let statusChanged = false;
    VACCINE_LIST.forEach(key => {
      const statusFieldName = `${key}_status`;
      if (changedValues.hasOwnProperty(statusFieldName)) {
        newStatuses[key] = changedValues[statusFieldName];
        statusChanged = true;
      }
    });
    if (statusChanged) {
      setVaccineStatuses(newStatuses);
    }
  };

  // Handle Edit Mode
  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    fetchStatus(); // Reload data to reset form
  };

  // --- Mark as Reviewed Logic ---
  const handleMarkReviewed = async () => {
    if (!user || !user._id) {
      message.error("Cannot mark as reviewed: User not logged in.");
      return;
    }
    const reviewData = { user: user._id, date: new Date().toISOString() };
    setLoading(true);
    try {
      await axios.patch(`/patients/${patientId}`, { vaccinationLastReviewed: reviewData });
      message.success('Vaccination History marked as reviewed.');
      setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
    } catch (err) {
      console.error("Error marking vaccination history as reviewed:", err);
      message.error(err.response?.data?.message || 'Failed to mark as reviewed.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has permission to modify
  const canModify = user && ['Admin', 'MD', 'Nursing'].includes(user.role);

  return (
    <Spin spinning={loading}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {/* Standard Action Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
        <Space>
          {canModify && !isEditMode && (
            <Button type="primary" icon={<EditOutlined />} onClick={handleEnterEditMode}>
              Update
            </Button>
          )}
          {canModify && isEditMode && (
            <Button onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          {canModify && (
            <Tooltip title="Mark this section as reviewed by you right now">
              <Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}>
                Mark as Reviewed
              </Button>
            </Tooltip>
          )}
        </Space>
        {/* Review Status Display */}
        <div style={{ textAlign: 'right' }}>
          {lastReviewed?.date ? (
            <Text type="secondary" style={{ fontSize: '0.85em' }}>
              Last Reviewed by {lastReviewed.user?.name || 'Unknown User'} on {moment(lastReviewed.date).format('L LT')}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: '0.85em' }}>Not yet reviewed</Text>
          )}
        </div>
      </div>

      {/* Changed layout to horizontal and added label/wrapper Col props */}
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ xs: 24, sm: 10, md: 8, lg: 7 }} // Responsive label width
        wrapperCol={{ xs: 24, sm: 14, md: 16, lg: 17, offset: 0 }} // Keep offset 0
        onFinish={handleSaveStatus}
        onValuesChange={handleValuesChange} // Add handler to track status changes
        disabled={!canModify || !isEditMode}
      >
        {VACCINE_LIST.map(vaccineKey => (
          <Form.Item
            key={vaccineKey}
            // No 'name' here, handled by nested items
            label={VACCINE_DISPLAY_NAMES[vaccineKey] || vaccineKey.toUpperCase()}
          >
            {/* Use Space to place Select and Input side-by-side */}
            <Space align="baseline">
              <Form.Item
                name={`${vaccineKey}_status`} // Name for status dropdown
                noStyle // Important: prevents default Form.Item styling/margin
              >
                <Select style={{ width: 150 }}> {/* Give Select a fixed width */}
                  {STATUS_OPTIONS.map(status => (
                    <Option key={status} value={status}>{status}</Option>
                  ))}
                </Select>
              </Form.Item>
              {/* Conditionally render Year input */}
              {vaccineStatuses[vaccineKey] === 'Given' && (
                <Form.Item
                  name={`${vaccineKey}_year`} // Name for year input
                  noStyle // Important: prevents default Form.Item styling/margin
                  rules={[{ pattern: /^\d{4}$/, message: 'Enter 4-digit year' }]} // Basic year validation
                >
                  <Input placeholder="Year" style={{ width: 80 }} maxLength={4} />
                </Form.Item>
              )}
            </Space>
          </Form.Item>
        ))}
        {/* 'Other' notes field - keep as is */}
        <Form.Item name="other" label="Other Vaccine Notes">
          <TextArea rows={2} placeholder="Notes on other vaccines..." />
        </Form.Item>

        {canModify && isEditMode && (
          <Form.Item style={{ marginTop: 20 }}> {/* Added margin top for spacing */}
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              Save Vaccination Status
            </Button>
          </Form.Item>
        )}
      </Form>
    </Spin>
  );
};

export default VaccinationStatusSection;
